import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PaperIconComponent } from '@ricoh-paper/angular';
import { ROOM_EQUIPMENT } from '../../../../../core/constants/meeting-services';
import { BookMeetingService } from '../../../../../core/services/book-meeting.service';
import { LocaleService } from '../../../../../core/services/locale.service';
import type { SpaceLayout } from '../../../../../core/services/book-meeting.service';

/**
 * State object emitted when the user changes any room service option.
 */
export interface RoomServices {
  layout: string | null;
  equipment: string[];
  av: string | null;
}

/**
 * RoomServicesSectionComponent — service chips and expandable editors
 * rendered inside each room card on the Book Meeting panel.
 *
 * RESPONSIBILITY: lets the user pick a room layout, request equipment,
 * and flag hybrid-AV or visitor requirements.
 *
 * USED BY: BookMeetingPanelComponent (inside the room card loop)
 *
 * AI CONTEXT:
 * - Service chips use emerald-green accent (#10b981 family) independent
 *   of the brand colour, matching the prototype design spec.
 * - Layout/equipment grids animate via max-height. All transitions
 *   honour prefers-reduced-motion.
 * - Keyboard accessible: chips are <button> with aria-expanded,
 *   layout buttons are <button>, equipment items use native <input>.
 */
@Component({
  selector: 'im-room-services',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, TranslocoPipe],
  template: `
    <!-- ── Service chips row ────────────────────────── -->
    <div class="rs__chips">
      <!-- Layout chip — only rendered when the space has authored layouts -->
      @if (showLayoutChip()) {
        <button
          class="rs__chip"
          [class.rs__chip--active]="selectedLayoutName() !== null"
          [class.rs__chip--open]="openSection() === 'layout'"
          [class.rs__chip--readonly]="readonly()"
          [attr.aria-expanded]="openSection() === 'layout'"
          [disabled]="readonly()"
          (click)="toggleSection('layout')">
          <paper-icon name="layout" [size]="13" />
          <span class="rs__chip-label">{{ 'viewer.book_meeting_panel.room_services.layout' | transloco }}</span>
          @if (selectedLayoutName(); as name) {
            <span class="rs__chip-value">{{ name }}</span>
          }
        </button>
      }

      <!-- Equipment chip — read-only summary of what the room actually has -->
      @if (showEquipmentChip()) {
        <button
          class="rs__chip"
          [class.rs__chip--open]="openSection() === 'equipment'"
          [class.rs__chip--readonly]="readonly()"
          [attr.aria-expanded]="openSection() === 'equipment'"
          [disabled]="readonly()"
          (click)="toggleSection('equipment')">
          <paper-icon name="video" [size]="13" />
          <span class="rs__chip-label">{{ 'viewer.book_meeting_panel.room_services.equipment' | transloco }}</span>
          <span class="rs__chip-value">{{ equipmentChips().length }}</span>
        </button>
      }

      <!-- Hybrid AV chip (only when remote attendees) -->
      @if (hasRemote()) {
        <button
          class="rs__chip"
          [class.rs__chip--active]="currentServices().av !== null"
          [class.rs__chip--readonly]="readonly()"
          [disabled]="readonly()"
          (click)="toggleAv()">
          <paper-icon name="video" [size]="13" />
          <span class="rs__chip-label">{{ 'viewer.book_meeting_panel.room_services.hybrid_av' | transloco }}</span>
        </button>
      }

      <!-- Visitors chip (only when external attendees) -->
      @if (hasExternal()) {
        <button
          class="rs__chip rs__chip--warning"
          [class.rs__chip--open]="openSection() === 'visitors'"
          [class.rs__chip--readonly]="readonly()"
          [attr.aria-expanded]="openSection() === 'visitors'"
          [disabled]="readonly()"
          (click)="toggleSection('visitors')">
          <paper-icon name="shield" [size]="13" />
          <span class="rs__chip-label">{{ 'viewer.book_meeting_panel.room_services.visitors' | transloco }}</span>
        </button>
      }
    </div>

    <!-- ── Layout expander ──────────────────────────── -->
    @if (openSection() === 'layout' && showLayoutChip() && !readonly()) {
      <div class="rs__expander">
        <div class="rs__layout-grid">
          @for (layout of layouts(); track layout.id) {
            <button
              class="rs__layout-btn"
              [class.rs__layout-btn--selected]="currentServices().layout === layout.id"
              (click)="selectLayout(layout)">
              <paper-icon [name]="layout.icon" [size]="16" />
              <span class="rs__layout-name">{{ layout.name }}</span>
              <span class="rs__layout-cap">
                <paper-icon name="users" [size]="9" />
                {{ layoutCapacity(layout) }}
              </span>
            </button>
          }
        </div>

        <!-- Setup/reset hint — pulled from the selected layout's own data -->
        @if (activeLayout(); as al) {
          @if (al.setupMin > 0 || al.resetMin > 0) {
            <div class="rs__hint rs__hint--warning">
              <paper-icon name="clock" [size]="12" />
              <span>{{ 'viewer.book_meeting_panel.room_services.facilities_setup' | transloco: { setup: al.setupMin, reset: al.resetMin } }}</span>
            </div>
          } @else {
            <div class="rs__hint">
              <paper-icon name="check-circle" [size]="12" />
              <span>{{ 'viewer.book_meeting_panel.room_services.no_setup_required' | transloco }}</span>
            </div>
          }
        }
      </div>
    }

    <!-- ── Equipment expander (read-only chip list) ── -->
    @if (openSection() === 'equipment' && showEquipmentChip() && !readonly()) {
      <div class="rs__expander">
        <div class="rs__equip-readonly">
          @for (chip of equipmentChips(); track chip.label) {
            <span class="rs__equip-readchip">
              <paper-icon [name]="chip.icon" [size]="11" />
              <span>{{ chip.label }}</span>
            </span>
          }
        </div>
        <div class="rs__hint">
          <paper-icon name="info" [size]="11" />
          <span>{{ 'viewer.book_meeting_panel.room_services.what_room_equipped_with' | transloco }}</span>
        </div>
        <!-- Legacy multi-select kept for callers still on the constants. -->
        @if (false) {
          @for (equip of equipment; track equip.id) {
            <label class="rs__equip-item">
              <input type="checkbox" [checked]="isEquipmentSelected(equip.id)"
                (change)="toggleEquipment(equip.id)" />
              <paper-icon [name]="equip.icon" [size]="12" />
              <span class="rs__equip-name">{{ equip.name }}</span>
            </label>
          }
        }
      </div>
    }
  `,
  styles: [`
    :host { display: block; }

    // ── Service Chips ──────────────────────────────────

    .rs__chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 8px;
    }

    .rs__chip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 5px 10px;
      border-radius: var(--radius-pill);
      border: 1px solid var(--pressed-overlay);
      background: var(--hover-overlay);
      color: var(--text-secondary);
      font-size: 11px;
      font-weight: 500;
      font-family: inherit;
      cursor: pointer;
      transition: all 0.15s ease;
      white-space: nowrap;

      &:hover {
        background: var(--focus-overlay);
        border-color: var(--border-med);
      }

      &:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }

      paper-icon { color: var(--text-tertiary); }
    }

    .rs__chip--active {
      background: rgba(16, 185, 129, 0.08);
      border-color: rgba(16, 185, 129, 0.22);

      paper-icon { color: var(--bmp-accent-text); }
    }

    .rs__chip--open {
      background: rgba(16, 185, 129, 0.12);
      border-color: rgba(16, 185, 129, 0.25);

      paper-icon { color: var(--bmp-accent-text); }
    }

    .rs__chip--warning {
      paper-icon { color: var(--warning); }
    }

    /* Read-only chip — non-interactive but still rendered as a static
       label so the user can see selected layout / equipment count / AV. */
    .rs__chip--readonly {
      cursor: default;
      opacity: 1; /* override :disabled visual */

      &:hover { background: var(--hover-overlay); }
    }
    .rs__chip:disabled.rs__chip--readonly {
      pointer-events: none;
    }

    .rs__chip-label {
      color: inherit;
    }

    .rs__chip-value {
      color: var(--bmp-accent-text);
      font-weight: 600;
    }

    // ── Expander Container ─────────────────────────────

    .rs__expander {
      margin-top: 8px;
      padding: 10px;
      background: var(--hover-overlay);
      border-top: 1px solid var(--divider);
      border-radius: var(--radius-sm);
      animation: rs-expand 200ms ease forwards;
    }

    @keyframes rs-expand {
      from { opacity: 0; max-height: 0; }
      to   { opacity: 1; max-height: 500px; }
    }

    // ── Layout Grid ────────────────────────────────────

    .rs__layout-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 6px;
    }

    .rs__layout-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 3px;
      padding: 8px 4px 6px;
      border-radius: var(--radius-sm);
      border: 1px solid var(--pressed-overlay);
      background: var(--hover-overlay);
      color: var(--text-secondary);
      cursor: pointer;
      font-family: inherit;
      transition: all 0.15s ease;

      &:hover {
        background: var(--focus-overlay);
        border-color: var(--border-med);
      }

      &:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }

      paper-icon { color: var(--text-tertiary); }
    }

    .rs__layout-btn--selected {
      background: rgba(16, 185, 129, 0.12);
      border-color: rgba(16, 185, 129, 0.25);
      color: var(--bmp-accent-text);

      paper-icon { color: var(--bmp-accent-text); }
    }

    .rs__layout-name {
      font-size: 11px;
      font-weight: 500;
      line-height: 1.2;
    }

    .rs__layout-cap {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      font-size: 10px;
      color: var(--text-tertiary);

      paper-icon { color: inherit; }
    }

    .rs__layout-btn--selected .rs__layout-cap {
      color: rgba(110, 231, 183, 0.7);
    }

    // ── Hints ──────────────────────────────────────────

    .rs__hint {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 8px;
      padding: 6px 8px;
      border-radius: 6px;
      font-size: 10.5px;
      color: var(--text-secondary);
      background: transparent;

      paper-icon { color: var(--text-tertiary); flex-shrink: 0; }
    }

    .rs__hint--warning {
      background: rgba(245, 158, 11, 0.05);
      border: 1px solid rgba(245, 158, 11, 0.14);
      color: var(--warning-text);

      paper-icon { color: var(--warning); }
    }

    // ── Read-only equipment chip list ───────────────────

    .rs__equip-readonly {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 8px;
    }

    .rs__equip-readchip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 4px 9px;
      border-radius: var(--radius-pill);
      background: var(--focus-overlay);
      border: 1px solid var(--glass-border);
      color: var(--text);
      font-size: 11px;

      paper-icon { color: var(--text-tertiary); }
    }

    // ── Equipment Grid ─────────────────────────────────

    .rs__equip-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 4px;
    }

    .rs__equip-item {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 8px;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.15s ease;

      &:hover {
        background: var(--focus-overlay);
      }

      paper-icon {
        color: var(--text-tertiary);
        flex-shrink: 0;
      }
    }

    .rs__equip-item--selected {
      background: rgba(16, 185, 129, 0.08);

      paper-icon { color: var(--bmp-accent-text); }
    }

    .rs__equip-checkbox {
      width: 14px;
      height: 14px;
      margin: 0;
      flex-shrink: 0;
      accent-color: var(--bmp-accent);
      cursor: pointer;
    }

    .rs__equip-name {
      font-size: 11.5px;
      color: var(--text-secondary);
      line-height: 1.2;
    }

    // ── Reduced Motion ─────────────────────────────────

    @media (prefers-reduced-motion: reduce) {
      .rs__chip { transition: none; }
      .rs__layout-btn { transition: none; }
      .rs__equip-item { transition: none; }
      .rs__expander { animation: none; }
    }
  `],
})
export class RoomServicesSectionComponent {
  private readonly bookMeetingService = inject(BookMeetingService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** Driven by BookMeetingService.mode — chips remain visible (they're
   *  display-only) but become non-interactive in 'view' mode and the
   *  expanders are suppressed. */
  protected readonly readonly = computed(
    () => this.bookMeetingService.mode() === 'view',
  );

  /** Room identifier */
  readonly roomId = input.required<string>();

  /** Current services state for this room */
  readonly services = input.required<RoomServices>();

  /** Whether any remote/online attendees are present */
  readonly hasRemote = input(false);

  /** Whether any external/visitor attendees are present */
  readonly hasExternal = input(false);

  /** Room's base capacity (kept for legacy callers; per-layout capacity now
   *  comes from the SpaceLayout itself). */
  readonly roomCapacity = input(0);

  /** Layouts authored on the space — drives the layout chip group. When the
   *  list is empty the layout chip + expander are hidden entirely. */
  readonly spaceLayouts = input<SpaceLayout[]>([]);

  /** Free-form equipment shape from the admin space-detail page. Used to
   *  drive read-only chips ("what this room has"). */
  readonly spaceEquipment = input<Record<string, unknown> | null | undefined>(null);

  /** Emitted when any service option changes */
  @Output() readonly servicesChange = new EventEmitter<RoomServices>();

  /** Which expander section is open (null = all collapsed) */
  protected readonly openSection = signal<'layout' | 'equipment' | 'visitors' | null>(null);

  /** Layouts to render — adapts the SpaceLayout shape to the chip data.
   *  When the space has none, returns an empty list so the layout chip is
   *  not rendered (per admin authoring exactly). */
  protected readonly layouts = computed(() =>
    (this.spaceLayouts() ?? []).map(l => ({
      id: l._id,
      name: l.name,
      icon: l.icon || 'grid',
      capacity: l.capacity,
      setupMin: l.setupMinutes ?? 0,
      resetMin: l.resetMinutes ?? 0,
    })),
  );

  /** Read-only equipment chips derived from space.equipmentConfig. */
  protected readonly equipmentChips = computed(() => {
    this.locale.locale();
    const cfg = this.spaceEquipment();
    if (!cfg || typeof cfg !== 'object') return [];
    const chips: { label: string; icon: string }[] = [];
    const cfgRecord = cfg as Record<string, unknown>;
    const displays = cfgRecord['displays'] as { count?: number; size?: string; type?: string } | undefined;
    if (displays && typeof displays === 'object') {
      const count = displays.count ?? 1;
      const size = displays.size ? ` ${displays.size}` : '';
      const type = displays.type ? ` (${displays.type})` : '';
      const displayWord = this.transloco.translate('viewer.book_meeting_panel.room_services.display');
      chips.push({ label: `${count}× ${displayWord}${size}${type}`, icon: 'monitor' });
    }
    const av = cfgRecord['audioVisual'];
    if (Array.isArray(av)) {
      for (const item of av) {
        if (typeof item === 'string' && item.trim()) {
          chips.push({ label: item, icon: 'video' });
        }
      }
    }
    // Capture any other primitive keys (e.g. "wireless presentation: yes").
    for (const [key, value] of Object.entries(cfg as Record<string, unknown>)) {
      if (key === 'displays' || key === 'audioVisual') continue;
      if (typeof value === 'string' && value.trim()) {
        chips.push({ label: `${humaniseKey(key)}: ${value}`, icon: 'check' });
      }
    }
    return chips;
  });

  /** Show the layout chip only when the space actually has layouts. */
  protected readonly showLayoutChip = computed(() => this.layouts().length > 0);

  /** Show the equipment chip only when the space has any equipment to list. */
  protected readonly showEquipmentChip = computed(() => this.equipmentChips().length > 0);

  /** Reference to the legacy equipment constants for the template — kept
   *  available for any caller that hasn't been migrated yet. */
  protected readonly equipment = ROOM_EQUIPMENT;

  /** Current services snapshot (convenience for template reads) */
  protected readonly currentServices = computed(() => this.services());

  /** Display name of the currently selected layout */
  protected readonly selectedLayoutName = computed(() => {
    const layoutId = this.services().layout;
    if (!layoutId) return null;
    return this.layouts().find(l => l.id === layoutId)?.name ?? null;
  });

  /** Count of selected equipment items (legacy — chips are read-only now) */
  protected readonly selectedEquipmentCount = computed(() =>
    this.services().equipment.length,
  );

  /** The active layout object (for hint display) */
  protected readonly activeLayout = computed(() => {
    const layoutId = this.services().layout;
    if (!layoutId) return null;
    return this.layouts().find(l => l.id === layoutId) ?? null;
  });

  // ── Actions ──────────────────────────────────────────

  toggleSection(section: 'layout' | 'equipment' | 'visitors'): void {
    this.openSection.set(this.openSection() === section ? null : section);
  }

  selectLayout(layout: { id: string; setupMin: number; resetMin: number }): void {
    const current = this.services();
    const newLayout = current.layout === layout.id ? null : layout.id;
    this.servicesChange.emit({
      ...current,
      layout: newLayout,
    });
  }

  toggleEquipment(equipId: string): void {
    const current = this.services();
    const selected = current.equipment.includes(equipId)
      ? current.equipment.filter(id => id !== equipId)
      : [...current.equipment, equipId];
    this.servicesChange.emit({
      ...current,
      equipment: selected,
    });
  }

  toggleAv(): void {
    const current = this.services();
    this.servicesChange.emit({
      ...current,
      av: current.av ? null : 'hybrid',
    });
  }

  isEquipmentSelected(equipId: string): boolean {
    return this.services().equipment.includes(equipId);
  }

  /** Per-layout capacity is now sourced from the layout itself, not derived
   *  from the room's base capacity. */
  layoutCapacity(layout: { capacity: number }): number {
    return Math.max(0, layout.capacity ?? 0);
  }
}

function humaniseKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, c => c.toUpperCase());
}
